import type { Editor } from "@tiptap/core";
import type { Scope } from "./types";

/**
 * Which scopes a keystroke should be offered to, most specific first.
 *
 * Focus does most of the work: opening a dialog blurs the editor, which clears
 * the active editor and drops `editor` out of the chain on its own. The DOM
 * probes below only cover the cases focus alone cannot distinguish.
 */

/**
 * Menus that steer with the keyboard and must win over the editor.
 *
 * `#slash-command` is mounted only while the slash menu is open. Anything else
 * opts in with `data-shortcut-scope="menu"`, so ordinary popovers and the
 * bubble menu — which are open constantly during normal editing — never
 * accidentally claim the scope.
 */
function isMenuOpen(): boolean {
  if (typeof document === "undefined") return false;
  return (
    document.getElementById("slash-command") !== null ||
    document.querySelector('[data-shortcut-scope="menu"]') !== null
  );
}

/**
 * Focus-trapped dialogs only. Keyed on `aria-modal`, which Radix sets on modal
 * dialog content but not on ordinary popovers, so the link and color popovers
 * inside the bubble menu stay out of `modal` scope.
 */
function isModalOpen(): boolean {
  if (typeof document === "undefined") return false;
  return document.querySelector('[aria-modal="true"]') !== null;
}

/**
 * Whole blocks are selected rather than a text caret sitting inside one.
 *
 * Reads the class the block-selection plugin already maintains for its own
 * styling, rather than reaching into plugin state — one owner for the fact,
 * and it stays correct if the plugin's internals change.
 */
function hasBlockSelection(editor: Editor): boolean {
  try {
    return editor.view.dom.classList.contains("block-selection-active");
  } catch {
    return false;
  }
}

export function resolveScopeChain(editor: Editor | null): Scope[] {
  if (isMenuOpen()) return ["menu", "global"];
  if (isModalOpen()) return ["modal", "global"];

  if (editor?.isFocused) {
    return hasBlockSelection(editor)
      ? ["block-selection", "editor", "global"]
      : ["editor", "global"];
  }

  return ["global"];
}

/**
 * Whether focus sits in a text field that is not part of an editor. Such fields
 * own their keystrokes unless a binding explicitly opts in with `allowInInput`.
 */
export function isTypingInPlainField(event: KeyboardEvent, editor: Editor | null): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;

  const tag = target.tagName;
  const editable =
    tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable === true;
  if (!editable) return false;

  // The focused editor's own contenteditable is not a "plain field".
  if (editor) {
    try {
      if (editor.view.dom === target || editor.view.dom.contains(target)) return false;
    } catch {
      // Editor view torn down mid-keystroke — treat as a plain field.
    }
  }
  return true;
}
